import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Checkbox,
  Icon,
  Skeleton,
  Text,
} from '@mntn/ui-components'
import type React from 'react'
import { useMemo } from 'react'
import { useNetworks } from '../../../../hooks/api/use-networks'
import { useFilter } from '../context/filter-context'

const SKELETON_ITEMS = [
  'network-skeleton-1',
  'network-skeleton-2',
  'network-skeleton-3',
  'network-skeleton-4',
  'network-skeleton-5',
  'network-skeleton-6',
  'network-skeleton-7',
  'network-skeleton-8',
]

const NetworkFilterSkeleton = () => (
  <div className="space-y-3">
    {SKELETON_ITEMS.map(key => (
      <div key={key} className="flex items-center space-x-3">
        <Skeleton className="w-4 h-4" />
        <Skeleton className="h-4" />
      </div>
    ))}
  </div>
)

export const NetworkFilter: React.FC = () => {
  const { filters, updateFilter, isSectionOpen, toggleSection } = useFilter()
  const { networks, isLoading } = useNetworks()
  const isOpen = isSectionOpen('networks')

  const selectedNetworks = useMemo(() => filters.networkIds || [], [filters.networkIds])

  const handleNetworkChange = (networkId: string, checked: boolean) => {
    const updatedNetworks = checked ? [...selectedNetworks, networkId] : selectedNetworks.filter(id => id !== networkId)

    updateFilter('networkIds', updatedNetworks.length > 0 ? updatedNetworks : undefined)
  }

  const selectedCount = selectedNetworks.length
  const allSelected = networks.length > 0 && selectedCount === networks.length

  const handleSelectAll = () => {
    if (allSelected) {
      updateFilter('networkIds', undefined)
    } else {
      updateFilter(
        'networkIds',
        networks.map(network => network.id),
      )
    }
  }

  const renderContent = () => {
    if (isLoading) {
      return <NetworkFilterSkeleton />
    }

    if (networks.length === 0) {
      return (
        <Text variant="base-sm" className="text-primary p-4">
          No networks available
        </Text>
      )
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Checkbox id="select-all-networks" checked={allSelected} onCheckedChange={handleSelectAll} />
          <Text variant="sm-sb" className="text-header">
            Select All ({networks.length})
          </Text>
        </div>

        <div className="space-y-3">
          {networks.map(network => (
            <div key={network.id} className="flex items-center space-x-3">
              <Checkbox
                id={`network-${network.id}`}
                checked={selectedNetworks.includes(network.id)}
                onCheckedChange={checked =>
                  // biome-ignore lint/nursery/noUnsafeTypeAssertion: RETIRED, do not copy. Fix the source type or narrow the value when touching this.
                  handleNetworkChange(network.id, checked as boolean)
                }
              />
              <Text variant="base-sm" className="text-header">
                {network.name}
              </Text>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Accordion
      type="single"
      collapsible
      value={isOpen ? 'networks' : ''}
      onValueChange={() => toggleSection('networks')}
      className="w-full"
    >
      <AccordionItem value="networks" className="border-0">
        <AccordionTrigger className="p-6 border-b border-borders-primary hover:no-underline">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <Text variant="lg-sb">Network</Text>
              {selectedCount > 0 && (
                <div className="bg-primary text-header-inverse rounded-full px-2 py-1 text-xs font-medium">
                  {selectedCount}
                </div>
              )}
            </div>
            <Icon
              name={isOpen ? 'Minus' : 'Plus'}
              className="h-6 w-6 text-primary shrink-0 transition-transform duration-200"
            />
          </div>
        </AccordionTrigger>
        <AccordionContent className="p-6 border-b border-borders-primary">{renderContent()}</AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
